'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useFeatureSwitch } from '@marketplace-web/shared/feature-switches'
import { useAbTest } from '@marketplace-web/shared/ab-tests'
import { useSession } from '@marketplace-web/shared/session'

import { useLatestCallback, UiState } from '@marketplace-web/shared/ui-state'
import {
  getClosetPromotions,
  transformClosetPromotionsResponse,
  ClosetModel,
  VasEntryPoint,
  VasEntryPointModel,
} from '@marketplace-web/domain/vas-data'

import { Screen } from 'constants/tracking/screens'

import { useCatalogContext } from 'pages/Catalog/CatalogProvider'
import { useItemsCatalogContext, useItemsCatalogQuery } from 'pages/Catalog/ItemsCatalogProvider'

import { ClosetPreviewLocation, getIsClosetPreviewSeen } from '../utils/closetPreviewCap'
import useFetchVasEntryPoints from './useFetchVasEntryPoints'

const CLOSET_PROMOTIONS_COUNT = 4

const useClosetPromotion = () => {
  const currentUserId = useSession()?.user?.id
  const { uiState } = useItemsCatalogContext()
  const {
    state: { searchSession, filters, dynamicFilters },
  } = useCatalogContext()
  const { page: currentPage } = useItemsCatalogQuery()
  const isClosetPromotionPreviewCatalogAbTestOn =
    useAbTest('closet_promotion_preview_catalog')?.variant === 'on'
  const isClosetPreviewSeen = getIsClosetPreviewSeen(ClosetPreviewLocation.Catalog)

  const { selectedDynamicFilters } = dynamicFilters
  const searchSessionId = searchSession.sessionId
  const dynamicFiltersUiState = dynamicFilters.ui.uiState

  const isClosetPromotionKillswitchEnabled = useFeatureSwitch('killswitch_closet_promotion_web')

  const userIds = useRef<Array<number>>([])
  const hasMore = useRef(true)
  const isPaginationEventRef = useRef(false)
  const isFilterChangeEventRef = useRef(false)
  const hasSearchSessionIdChangedRef = useRef(false)

  const [closets, setClosets] = useState<Array<ClosetModel>>([])

  const resetClosets = () => {
    userIds.current = []
    hasMore.current = true
  }

  useEffect(() => {
    isPaginationEventRef.current = true
  }, [currentPage])

  useEffect(() => {
    isFilterChangeEventRef.current = true
    resetClosets()
  }, [filters, selectedDynamicFilters])

  useEffect(() => {
    hasSearchSessionIdChangedRef.current = true
  }, [searchSessionId])

  const { data: vasEntryPoints, fetchClosetPromotionEntryPoint } = useFetchVasEntryPoints(
    [VasEntryPoint.PromotedClosets],
    isClosetPromotionPreviewCatalogAbTestOn && !isClosetPreviewSeen,
  )

  const fetchClosetPromotions = useCallback(
    async (isPreview: boolean) => {
      const shouldFetch = !isClosetPromotionKillswitchEnabled && hasMore.current

      if (!shouldFetch) {
        setClosets([])

        return
      }

      const response = await getClosetPromotions({
        excludedUserIds: userIds.current,
        perPage: CLOSET_PROMOTIONS_COUNT,
        screenName: Screen.Catalog,
        searchSessionId,
        isPreview: isPreview || undefined,
        selectedDynamicFilters,
        ...filters,
      })

      if ('errors' in response) {
        setClosets([])

        return
      }

      hasMore.current = response.page_info.has_more
      const transformedClosets = transformClosetPromotionsResponse(response)

      if (transformedClosets[0]) {
        const isViewingOwnCloset = currentUserId === transformedClosets[0].user.id

        if (isPreview && isViewingOwnCloset) transformedClosets[0].isPreview = true
      }

      userIds.current = [...userIds.current, ...transformedClosets.map(closet => closet.user.id)]
      setClosets(transformedClosets)
    },
    [
      isClosetPromotionKillswitchEnabled,
      searchSessionId,
      selectedDynamicFilters,
      filters,
      currentUserId,
    ],
  )

  const fetchData = useLatestCallback(async () => {
    let closetPromotionEntryPoint: VasEntryPointModel | undefined

    if (!vasEntryPoints && !isClosetPreviewSeen && isClosetPromotionPreviewCatalogAbTestOn) {
      closetPromotionEntryPoint = await fetchClosetPromotionEntryPoint()
    }

    fetchClosetPromotions(!!closetPromotionEntryPoint)
  })

  useEffect(() => {
    if (uiState !== UiState.Success) return
    if (dynamicFiltersUiState !== UiState.Success) return
    if (!isFilterChangeEventRef.current && !isPaginationEventRef.current) return
    if (isFilterChangeEventRef.current && !hasSearchSessionIdChangedRef.current) return

    fetchData()

    if (isFilterChangeEventRef.current) isFilterChangeEventRef.current = false
    if (isPaginationEventRef.current) isPaginationEventRef.current = false
    hasSearchSessionIdChangedRef.current = false
  }, [uiState, dynamicFiltersUiState, searchSessionId, filters, selectedDynamicFilters, fetchData])

  const getClosetListWithBanner = (closetList?: Array<ClosetModel>) => {
    if (!closetList?.length || !closetList) return []

    const closetListClone = [...closetList]

    if (closetListClone[0]) {
      closetListClone[0] = { ...closetListClone[0] }
      closetListClone[0].showBanner = true
    }

    return closetListClone
  }

  const closetsWithBanner = getClosetListWithBanner(closets)

  return { closetsWithBanner, vasEntryPoints }
}

export default useClosetPromotion
